import { computed, ref } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { MESSAGES_INBOX_QUERY } from '~/graphql/inbox/queries'
import { useAuthStore } from '@/stores/authStore'

/**
 * The MESSAGES inbox (V2) — module-level singleton, one poll for five sections.
 * ------------------------------------------------------------------------------
 * Replaces the three separate 15s polls the V1 page runs (case / staff / contact
 * chat) with a single round-trip that ALSO carries the two sections that never
 * had a home: missed calls and messages from numbers we don't know.
 *
 * The three entity sections come back from the very same server functions the
 * V1 page uses, so a flag flip can never show two different numbers for the
 * same thing.
 *
 * Same manners as the chat pollers: silent while the tab is hidden, never
 * overlapping, optimistic removal on dismiss.
 */

const POLL_MS = 15_000

const EMPTY_TOTALS = {
  missedCalls: 0,
  cases: 0,
  contacts: 0,
  staff: 0,
  unknown: 0,
  all: 0,
}

const _missedCalls = ref([])
const _cases = ref([])
const _companies = ref([])
const _contacts = ref([])
const _staff = ref([])
const _unknown = ref([])
const _totals = ref({ ...EMPTY_TOTALS })
const _loading = ref(false)
const _loaded = ref(false)

let _running = false
let _timer = null
let _inFlight = false
let _client = null
let _visibilityBound = false

async function _fetch(resolveClient) {
  if (_inFlight || !resolveClient) return
  _inFlight = true
  if (!_loaded.value) _loading.value = true
  try {
    const { data } = await resolveClient().query({
      query: MESSAGES_INBOX_QUERY,
      fetchPolicy: 'network-only',
    })

    const p = data?.getMessagesInbox ?? {}

    _missedCalls.value = p.missedCalls ?? []
    _cases.value = p.cases ?? []
    _companies.value = p.companies ?? []
    _contacts.value = p.contacts ?? []
    _staff.value = p.staff ?? []
    _unknown.value = p.unknown ?? []
    _totals.value = { ...EMPTY_TOTALS, ...(p.totals ?? {}) }
    _loaded.value = true
  }
  catch { /* transient — the next tick retries */ }
  finally {
    _inFlight = false
    _loading.value = false
  }
}

function _startTimer() {
  if (_timer || !_running || !_client) return
  _timer = setInterval(() => _fetch(_client), POLL_MS)
}

function _stopTimer() {
  if (_timer) {
    clearInterval(_timer)
    _timer = null
  }
}

function _onVisibility() {
  if (document.hidden) {
    _stopTimer()

    return
  }
  if (!_running) return
  _fetch(_client)
  _startTimer()
}

/** Reset on logout so the next user never inherits this badge. */
export function resetMessagesInbox() {
  _running = false
  _stopTimer()

  if (_visibilityBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _onVisibility)
    _visibilityBound = false
  }

  _client = null
  _missedCalls.value = []
  _cases.value = []
  _companies.value = []
  _contacts.value = []
  _staff.value = []
  _unknown.value = []
  _totals.value = { ...EMPTY_TOTALS }
  _loaded.value = false
}

export function useMessagesInbox() {
  const { resolveClient } = useApolloClient()
  const authStore = useAuthStore()

  function start() {
    if (!authStore.isLeadAgencyUser) return

    _client = resolveClient
    if (_running) return
    _running = true

    if (typeof document !== 'undefined' && !_visibilityBound) {
      document.addEventListener('visibilitychange', _onVisibility)
      _visibilityBound = true
    }

    _fetch(_client)
    _startTimer()
  }

  function refresh() {
    if (authStore.isLeadAgencyUser) _fetch(_client ?? resolveClient)
  }

  /**
   * Optimistic removal — the click drops the row and its count NOW; the
   * mutation + refresh that follow true it up (a failure just brings it back
   * on the next poll).
   *
   * @param section  key of the section list ('missedCalls' | 'cases' | …)
   * @param match    predicate identifying the row to drop
   */
  function dismiss(section, match) {
    const lists = {
      missedCalls: _missedCalls,
      cases: _cases,
      contacts: _contacts,
      staff: _staff,
      unknown: _unknown,
    }

    const list = lists[section]
    if (!list) return

    const row = list.value.find(match)
    if (!row) return

    list.value = list.value.filter(r => r !== row)

    // Every row carries how many things it stands for — calls for a caller,
    // messages for a thread.
    const drop = row.count ?? 1

    _totals.value = {
      ..._totals.value,
      [section]: Math.max(0, (_totals.value[section] ?? 0) - drop),
      all: Math.max(0, (_totals.value.all ?? 0) - drop),
    }
  }

  return {
    missedCalls: computed(() => _missedCalls.value),
    cases: computed(() => _cases.value),
    companies: computed(() => _companies.value),
    contacts: computed(() => _contacts.value),
    staff: computed(() => _staff.value),
    unknown: computed(() => _unknown.value),
    totals: computed(() => _totals.value),
    total: computed(() => _totals.value.all ?? 0),
    loading: computed(() => _loading.value),
    loaded: computed(() => _loaded.value),
    start,
    refresh,
    dismiss,
    stop: resetMessagesInbox,
  }
}
